<template>
  <div class="event-page" v-if="eventData && eventData.id">
    <div class="event-header">
      <div class="event-info">
        <h1 class="event-title">{{ eventName }}</h1>
        <div class="date-navigation">
          <button
            class="nav-btn"
            @click="page !== 0 && page--"
            :disabled="page === 0"
            :class="{ disabled: page === 0 }"
          >
            ‹
          </button>
          <span class="date-range">{{ eventRange }}</span>
          <button
            class="nav-btn"
            @click="page !== pageNumbers && page++"
            :disabled="page === pageNumbers"
            :class="{ disabled: page === pageNumbers }"
          >
            ›
          </button>
        </div>
      </div>

      <div class="header-actions">
        <button
          @click="copyLink"
          class="copy-btn"
          :class="copyState"
          :title="copyTooltip"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M8 17a4 4 0 0 1-4-4v-5a4 4 0 0 1 4-4h5a4 4 0 0 1 4 4" />
            <path d="M16 7a4 4 0 0 1 4 4v5a4 4 0 0 1-4 4h-5a4 4 0 0 1-4-4" />
          </svg>
          {{ copyLabel }}
        </button>

        <button
          @click="clearAll"
          class="clear-btn"
          title="Clear your availability"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
            />
          </svg>
          Clear
        </button>
      </div>
    </div>

    <div class="event-content">
      <div class="calendar-container">
        <Calendar :userName="userName" :page="page" />
      </div>

      <div class="sidebar">
        <Participants />
        <EventDetails />
      </div>
    </div>
  </div>
</template>

<script>
import Calendar from "../components/Calendar.vue";
import Participants from "../components/Participants";
import EventDetails from "../components/EventDetails";
import { useStore } from "vuex";
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { ActionTypes } from "../store/actions";

// Random name generator
const generateRandomName = () => {
  const adjectives = [
    "Happy",
    "Lucky",
    "Brave",
    "Swift",
    "Calm",
    "Bright",
    "Smart",
    "Kind",
    "Bold",
    "Cool",
  ];
  const nouns = [
    "Panda",
    "Tiger",
    "Eagle",
    "Dolphin",
    "Fox",
    "Wolf",
    "Bear",
    "Lion",
    "Hawk",
    "Owl",
  ];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return `${adj} ${noun}`;
};

export default {
  components: {
    Calendar,
    Participants,
    EventDetails,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const clearAll = () => {
      store.dispatch("REMOVE_SELF_AVAILABILITY");
    };
    const copyState = ref("idle");
    const copyLabel = ref("Copy Link");
    const copyTooltip = computed(() => {
      if (copyState.value === "success") {
        return "Link copied to clipboard";
      }
      if (copyState.value === "error") {
        return "Copy failed, click to try again";
      }
      return "Copy this event link";
    });
    let copyResetTimeout;
    const resetCopyState = () => {
      copyState.value = "idle";
      copyLabel.value = "Copy Link";
    };
    const copyLink = async () => {
      const currentUrl = window.location.href;
      if (copyResetTimeout) {
        clearTimeout(copyResetTimeout);
        copyResetTimeout = undefined;
      }
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(currentUrl);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = currentUrl;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          const successful = document.execCommand("copy");
          document.body.removeChild(textarea);
          if (!successful) {
            throw new Error("Fallback copy command was unsuccessful");
          }
        }
        copyState.value = "success";
        copyLabel.value = "Link copied!";
      } catch (error) {
        console.error("Failed to copy event link:", error);
        copyState.value = "error";
        copyLabel.value = "Copy failed";
      } finally {
        copyResetTimeout = window.setTimeout(() => {
          resetCopyState();
          copyResetTimeout = undefined;
        }, 2000);
      }
    };

    onUnmounted(() => {
      if (copyResetTimeout) {
        clearTimeout(copyResetTimeout);
      }
    });

    const page = ref(0);

    // Load event data and create user with random name
    onMounted(async () => {
      await store.dispatch(ActionTypes.loadAvailabilities, route.params.id);

      // Check if user already exists in localStorage
      const existingUserID = localStorage.getItem(route.params.id);
      if (!existingUserID) {
        // Generate random name and add user
        const randomName = generateRandomName();
        await store.dispatch(ActionTypes.addUserName, {
          username: randomName,
          eventID: route.params.id,
        });
      }
    });

    return {
      clearAll,
      copyLink,
      copyState,
      copyLabel,
      copyTooltip,
      page,
      eventRange: computed(() => store.getters.getTopLabel),
      userName: computed(() => store.state.userName),
      pageNumbers: computed(() => store.getters.getPageNumbers),
      eventName: computed(() => store.getters.getEventName),
      usernameExist: computed(() => store.getters.usernameExist),
      eventData: computed(() => store.getters.getEventData),
    };
  },
};
</script>

<style scoped>
.event-page {
  padding: 2rem 0 3rem;
}

.event-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f5f5f5;
}

.event-info {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.event-title {
  font-size: 1.75rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 1.5rem;
  color: #404040;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.nav-btn:hover:not(:disabled) {
  background: #e5e5e5;
  border-color: #d4d4d4;
}

.nav-btn:disabled,
.nav-btn.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.date-range {
  font-size: 0.95rem;
  color: #737373;
  min-width: 120px;
  text-align: center;
  font-weight: 400;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.copy-btn,
.clear-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #737373;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn:hover,
.clear-btn:hover {
  background: #f5f5f5;
  border-color: #d4d4d4;
  color: #404040;
}

.copy-btn.success {
  background: #ecfdf5;
  border-color: #bbf7d0;
  color: #047857;
}

.copy-btn.error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

.event-content {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 2rem;
  align-items: start;
}

.calendar-container {
  min-width: 0;
  overflow: visible;
  width: 100%;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

@media (max-width: 1024px) {
  .event-content {
    grid-template-columns: 1fr;
  }

  .sidebar {
    order: -1;
  }
}

@media (max-width: 768px) {
  .event-page {
    padding: 1.5rem 0 2rem;
  }

  .event-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .event-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .event-title {
    font-size: 1.5rem;
  }

  .date-navigation {
    width: 100%;
    justify-content: center;
  }

  .clear-btn {
    width: 100%;
    justify-content: center;
  }

  .event-content {
    gap: 1.5rem;
  }
}
</style>
